// @ts-strict-ignore
import {Component} from "@angular/core";
import {Location} from "@angular/common";
import {
	SimMaterialDialogService,
	SimMediasetService,
	NotificationService,
	SimSystemService,
	SimTrainingRoomService,
	SimUtilityService,
	SimWakeOnLanRequestRetryService,
	TrackByMethods,
} from "@services";
import {
	ActivityState,
	CameraResolutionType,
	CameraViewMode,
	ConnectivityState,
	DisplayMode,
	Media,
	MediaType,
	PlayerMapping,
	RoomCapability,
	SignageMode,
	TrainingRoom,
	TrainingRoomGroupView,
	TrainingRoomType,
} from "@models";
import {EventEmitterService} from "@event-emitter";
import {zoomInOnEnterAnimation} from "angular-animations";
import {TranslateService} from "@ngx-translate/core";
import {CdkDragDrop} from "@angular/cdk/drag-drop";
import {forkJoin, timer} from "rxjs";
import {Translations} from "@translations";

export enum DigitalSignageDraggedItemType {
	Media,
	Room,
}

@Component({
	selector: "sim-digital-signage-configuration-page",
	templateUrl: "./digital-signage-configuration-page.component.html",
	styleUrls: ["./digital-signage-configuration-page.component.scss"],
	animations: [zoomInOnEnterAnimation({duration: 250})],
	providers: [EventEmitterService, SimWakeOnLanRequestRetryService],
	standalone: false,
})
export class DigitalSignageConfigurationPageComponent {
	T = Translations;
	DraggedItemType = DigitalSignageDraggedItemType;

	public trainingRoomsGrouped: TrainingRoomGroupView[];
	availableData: {-readonly [key in keyof typeof MediaType]: Media[]};

	SignageMode = SignageMode;
	TrackByMethods = TrackByMethods;
	ActivityState = ActivityState;
	ConnectivityState = ConnectivityState;
	MediaType = MediaType;
	RoomCapability = RoomCapability;
	CameraViewMode = CameraViewMode;
	DisplayMode = DisplayMode;
	CameraResolutionType = CameraResolutionType;

	expandedTrainingRooms: Set<string> = new Set<string>();
	selectedTabIndex: number;
	//Whether the first training Room panel has been opened already
	alreadyOpened: boolean;
	loadingAvailableData: boolean;
	mediaTypes: MediaType[] = [MediaType.Image, MediaType.Video, MediaType.PowerPoint, MediaType.Audio, MediaType.PDF];
	// mediaTypes: MediaType[] = [MediaType.PDF];

	pageSize: number = 10;
	currentPagesPerMediaType: Map<MediaType, {page: number; end: boolean}> = new Map<MediaType, {page: number; end: boolean}>();
	loadingNextPage: boolean = false;

	playerBusy: Map<string, {restart: boolean}> = new Map<string, {restart: boolean}>();

	playerMediaLoading: Map<string, boolean> = new Map<string, boolean>();

	constructor(
		private _location: Location,
		protected _simTrainingRoomService: SimTrainingRoomService,
		private _eventEmitter: EventEmitterService,
		private _simMediaSetService: SimMediasetService,
		private _simNotificationService: NotificationService,
		private _translate: TranslateService,
		public _simSystemService: SimSystemService,
		private _simMaterialDialogService: SimMaterialDialogService,
		protected _simWakeOnLanService: SimWakeOnLanRequestRetryService,
	) {
		//Init page/mediatype map
		this.mediaTypes.forEach((type) => this.currentPagesPerMediaType.set(type, {page: 0, end: false}));

		this.initialize();

		this._eventEmitter.onPlayerStateChanged.subscribe((args) => {
			this._simWakeOnLanService.handleStateUpdate(args.player);

			this.trainingRoomsGrouped.forEach((groupView) => {
				groupView.trainingRooms.forEach((room) => {
					room.digitalSignagePlayers.forEach((player) => {
						if (player.id === args.player.id) {
							SimUtilityService.copy(args.player, player);
						}
					});
				});
			});
		});

		this.initAvailableData();
	}

	public initAvailableData() {
		this.availableData = <any>{};
		this.loadingAvailableData = true;

		let mediaRequests = [];
		this.mediaTypes.forEach((mediaType) => {
			mediaRequests.push(this.getMedia(mediaType, 0, this.pageSize));
		});

		forkJoin(mediaRequests).subscribe({
			next: () => {
				//this.prepareMetaData(medias);
				this.loadingAvailableData = false;
			},
			error: (err) => {
				this._simNotificationService.notifyError(this._translate.instant(this.T.SCENARIO_LIBRARY_SCENARIO_MEDIA_LOADING_ERROR));
				this.loadingAvailableData = false;
			},
		});
	}

	private async getMedia(mediaType: MediaType, page: number, pageSize: number) {
		const mediaResult: Media[] = await this._simMediaSetService.listMedias(mediaType, "", page, pageSize);

		if (!this.availableData[mediaType]) {
			if (mediaResult?.length < this.pageSize) {
				this.currentPagesPerMediaType.set(mediaType, {
					page: page,
					end: true,
				});
			}
			this.availableData[mediaType] = mediaResult;
		} else {
			if (mediaResult?.length < this.pageSize) {
				this.currentPagesPerMediaType.set(mediaType, {
					page: page,
					end: true,
				});
				this.availableData[mediaType] = this.availableData[mediaType].concat(mediaResult);
			} else {
				this.availableData[mediaType] = this.availableData[mediaType].concat(mediaResult);
			}
		}
	}

	historyBack() {
		this._location.back();
	}

	private initialize() {
		this._simTrainingRoomService
			.getTrainingRoomsGroupedWithFilter(TrainingRoomType.Undefined, RoomCapability.DigitalSignage)
			.then((trainingRoomsGroupedRes) => {
				this.trainingRoomsGrouped = trainingRoomsGroupedRes;
				this.trainingRoomsGrouped.forEach((groupView) => {
					let trainingRoomId = groupView?.trainingRooms?.[0]?.id;
					if (trainingRoomId) {
						if (!this.alreadyOpened) {
							this.expandedTrainingRooms.add(trainingRoomId);
							this.alreadyOpened = true;
						}
					}
				});
			});
	}

	toggleExpandTrainingRoom(trainingRoom: TrainingRoom, trainingRoomGroup: TrainingRoomGroupView) {
		if (this.expandedTrainingRooms.has(trainingRoom.id)) {
			this.expandedTrainingRooms.delete(trainingRoom?.id);
		} else {
			//Check if another traininngroom within this group is already open and close it
			trainingRoomGroup.trainingRooms.forEach((room) => {
				if (this.expandedTrainingRooms.has(room.id)) {
					this.expandedTrainingRooms.delete(room.id);
				}
			});

			this.expandedTrainingRooms.add(trainingRoom.id);
		}
	}

	test() {}

	allPlayerDropLists(): string[] {
		let result: string[] = [];
		if (this.trainingRoomsGrouped) {
			this.trainingRoomsGrouped.forEach((roomGroupView) =>
				roomGroupView.trainingRooms.forEach((room) => {
					room.digitalSignagePlayers.forEach((player) => result.push("dropListRoomId-" + room.id + "PlayerId-" + player.id));
				}),
			);
		}
		return result;
	}

	elementDropped(event: CdkDragDrop<any, any>, player: PlayerMapping) {
		let dragData = event.item?.data;
		if (dragData?.type === DigitalSignageDraggedItemType.Media) {
			let media = dragData?.item as Media;
			player.signage = {
				mode: SignageMode.Media,
				mediaId: media?.id,
				trainingRoomId: null,
				roomLayoutPreset: 0,
			};
			this._simSystemService.updatePlayerMapping(player).then();

			if (player.signage.mediaId != media?.id) {
				this.playerMediaLoading.set(player.id, true);
			}
		} else if (dragData?.type === DigitalSignageDraggedItemType.Room) {
			let room = dragData?.item as TrainingRoom;
			player.signage = {
				mode: SignageMode.TrainingRoom,
				trainingRoomId: room?.id,
				mediaId: null,
				roomLayoutPreset: 0,
			};
			this._simSystemService.updatePlayerMapping(player).then();
		}
	}

	removeSignage(player: PlayerMapping) {
		player.signage = null;
		this._simSystemService.updatePlayerMapping(player);
	}

	mediaHolderScrolled(event: Event, mediaType: MediaType) {
		if (
			// @ts-ignore
			event.target.offsetHeight + event.target.scrollTop >=
			// @ts-ignore
			event.target.scrollHeight
		) {
			if (!this.currentPagesPerMediaType.get(mediaType).end) {
				//Load next page of this media type
				this.loadingNextPage = true;
				this.currentPagesPerMediaType.set(mediaType, {
					page: this.currentPagesPerMediaType.get(mediaType).page + 1,
					end: false,
				});
				this.getMedia(mediaType, this.currentPagesPerMediaType.get(mediaType).page, this.pageSize).then(() => {
					this.loadingNextPage = false;
				});
			}
		}
	}

	reloadPlayer(digitalSignagePlayer: PlayerMapping) {
		this._simMaterialDialogService
			.openConfirmationDialog({
				title: this._translate.instant(this.T.WEB_DEBRIEFING_RESTART_TITLE),
				description: this._translate.instant(this.T.WEB_DEBRIEFING_RESTART_DESCRIPTION),
				okText: this._translate.instant(this.T.WEB_DEBRIEFING_RESTART_OK_TEXT),
				cancelText: this._translate.instant(this.T.WEB_DEBRIEFING_RESTART_CANCEL_TEXT),
			})
			.then((result) => {
				if (result.action === "ok") {
					this.playerBusy.set(digitalSignagePlayer.id, {
						restart: true,
					});

					forkJoin([this._simSystemService.reloadClient(digitalSignagePlayer?.webSocket?.connectionId), timer(2000)]).subscribe(
						([success]) => {
							this.playerBusy.delete(digitalSignagePlayer.id);
						},
					);
				}
			});
	}

	changeRoomLayout(digitalSignagePlayer: PlayerMapping, i: number) {
		digitalSignagePlayer.signage.roomLayoutPreset = i;
		this._simSystemService.updatePlayerMapping(digitalSignagePlayer);
	}

	asMedias(data: any[]) {
		return <Media[]>data;
	}

	mediaLoaded(digitalSignagePlayer: PlayerMapping) {
		this.playerMediaLoading.delete(digitalSignagePlayer.id);
	}
}
